import { useMemo } from 'react';

export const UI_MESSAGE_STREAM_FLAG_STORAGE_KEY = 'askrichard.flags.ui-message-stream';
export const DEBUG_DETAILS_FLAG_STORAGE_KEY = 'askrichard.flags.debug-details';

type FlagStorage = Pick<Storage, 'getItem' | 'setItem'>;

interface QueryFlag {
  param: string;
  onValue: string;
  offValue: string;
  storageKey: string;
}

const UI_MESSAGE_STREAM_FLAG: QueryFlag = {
  param: 'wire',
  onValue: 'ui',
  offValue: 'legacy',
  storageKey: UI_MESSAGE_STREAM_FLAG_STORAGE_KEY,
};

const DEBUG_DETAILS_FLAG: QueryFlag = {
  param: 'debug',
  onValue: '1',
  offValue: '0',
  storageKey: DEBUG_DETAILS_FLAG_STORAGE_KEY,
};

function browserStorage(): FlagStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function resolveQueryFlag(flag: QueryFlag, search: string, storage: FlagStorage | null, fallback: boolean): boolean {
  const requested = new URLSearchParams(search).get(flag.param);
  if (requested === flag.onValue || requested === flag.offValue) {
    const enabled = requested === flag.onValue;
    try {
      storage?.setItem(flag.storageKey, String(enabled));
    } catch {
      return enabled;
    }
    return enabled;
  }
  try {
    const stored = storage?.getItem(flag.storageKey);
    return stored === 'true' || stored === 'false' ? stored === 'true' : fallback;
  } catch {
    return fallback;
  }
}

export function resolveUiMessageStreamFlag(search: string, storage: FlagStorage | null): boolean {
  return resolveQueryFlag(UI_MESSAGE_STREAM_FLAG, search, storage, true);
}

export function resolveDebugDetailsFlag(search: string, storage: FlagStorage | null): boolean {
  return resolveQueryFlag(DEBUG_DETAILS_FLAG, search, storage, false);
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

export function useUiMessageStreamFlag(): boolean {
  return useMemo(() => resolveUiMessageStreamFlag(currentSearch(), browserStorage()), []);
}

export function useDebugDetailsFlag(): boolean {
  return useMemo(() => resolveDebugDetailsFlag(currentSearch(), browserStorage()), []);
}
